import {
  Building,
  getAllBuildings,
  getBuildingEntities,
  getDefaultBuilding,
  getIsMeetingsCalendarEnabled,
  getUserCheckInStatus,
  getHasUserGivenCalendarConsent,
  getRoomScheduleInformationForRoomId,
  getWorkdayByDate,
  getSuggestionDataForRoomId,
  getIsLoadingUserCheckIn,
  getIsLoadingWorkspaceReservations,
} from '../../data';
import createCachedSelector from 're-reselect';
import {RootState} from '../../rootReducer';
import {add, addMinutes, endOfDay, isWithinInterval, parseISO} from 'date-fns';
import {createSelector} from '@reduxjs/toolkit';
import {getIsHybridMeetingsFeatureEnabled, getIsOccupancyEnabled} from '../../infrastructure';
import {CheckInEventType} from '@lib/infrastructure';
import {getIsConsentTileVisible} from '../meetings';
import {Interval, Timeslot} from './types';
import {getRoomAvailabilityPeriodOfInterest} from '../meetings';
import {
  calculateDynamicFreeTimeslot,
  getLastAboutToEndTimeslot,
  getTimeSlotsThatStartsWithinInterval,
  mapScheduleInformationToTimeslot,
} from './helpers';
import {LocalDateString} from '../../data/timeslots/apiTypes';
import {nonEmpty} from '@lib/utils';

export const getShouldShowHereAndNowConnectYourCalendarFullScreen = createSelector(
  getIsMeetingsCalendarEnabled,
  getIsHybridMeetingsFeatureEnabled,
  getHasUserGivenCalendarConsent,
  getIsOccupancyEnabled,
  (
    isCalendarIntegrationEnabled,
    isHybridMeetingsFeatureEnabled,
    hasUserGivenCalendarConsent,

    isOccupancyEnabled,
  ) => {
    const meetingConsentPreconditions =
      isCalendarIntegrationEnabled && isHybridMeetingsFeatureEnabled && !hasUserGivenCalendarConsent;

    return meetingConsentPreconditions && !isOccupancyEnabled;
  },
);

export const getIsBuildingByCheckInOrWorkspaceReservationLoading = createSelector(
  getIsLoadingUserCheckIn,
  getIsLoadingWorkspaceReservations,
  (isLoadingUserCheckIn, isLoadingWorkspaceReservations) => isLoadingUserCheckIn || isLoadingWorkspaceReservations,
);

export const getBuildingByCheckInOrWorkspaceReservation = createCachedSelector(
  getUserCheckInStatus,
  getBuildingEntities,
  getAllBuildings,
  getDefaultBuilding,
  getWorkdayByDate,
  (checkInStatus, buildingEntities, allBuildings, defaultBuilding, workday): Building | undefined => {
    const checkInBuilding =
      checkInStatus?.eventType === CheckInEventType.AtTheOffice
        ? buildingEntities[checkInStatus.buildingId ?? '']
        : undefined;

    if (checkInBuilding) return checkInBuilding;

    const workdayBuilding = buildingEntities[workday?.nodeId ?? ''];
    return workdayBuilding ?? defaultBuilding ?? allBuildings[0];
  },
)((_state, selectedDate) => selectedDate);

export const getBuildingsByWorkday_Timeslots = createCachedSelector(
  getBuildingEntities,
  getDefaultBuilding,
  (state: RootState, dateKey: LocalDateString) => state.data.timeslots.workdaysByDate[dateKey],
  (buildingEntities, defaultBuilding, workday): Building[] => {
    if (workday?.status !== 'fulfilled') {
      return defaultBuilding ? [defaultBuilding] : [];
    }

    return nonEmpty(
      workday.data.map((wd) => (wd.status === 'OfficeDay' ? buildingEntities[wd.buildingId] : undefined)),
    );
  },
)((_state, selectedDate) => selectedDate);

export const getShouldShowHereAndNowCalendarConsentBanner = createSelector(
  getIsConsentTileVisible,
  getIsOccupancyEnabled,
  (shouldShowCalendarConsentTile, isOccupancyEnabled) => shouldShowCalendarConsentTile && isOccupancyEnabled,
);

export const getFirstBookingWhithinInterval = createCachedSelector(
  getRoomScheduleInformationForRoomId,
  (_state: RootState, _buildingId: string, _roomId: string, interval?: Interval) => interval,
  (scheduleInformation, interval) => getTimeSlotsThatStartsWithinInterval(scheduleInformation, interval)?.[0],
)(
  (_: RootState, roomId: string, interval?: Interval) =>
    `${roomId}:${interval?.minStartDateString ?? ''}:${interval?.maxStartDateString ?? ''}`,
);

export const getFirstBookingAfterAboutToEndPeriod = createCachedSelector(
  getRoomAvailabilityPeriodOfInterest,
  getRoomScheduleInformationForRoomId,
  (_state: RootState, _buildingId: string, _roomId: string, start: string) => start,
  (roomAvailabilityPeriodOfInterest, scheduleInformation, start) =>
    getTimeSlotsThatStartsWithinInterval(scheduleInformation, {
      minStartDateString: addMinutes(parseISO(start), roomAvailabilityPeriodOfInterest).toISOString(),
      maxStartDateString: endOfDay(parseISO(start)).toISOString(),
    })?.[0],
)((_: RootState, roomId: string, start: string) => `${roomId}:${start}`);

export const getLastAboutToEndBooking = createCachedSelector(
  getRoomAvailabilityPeriodOfInterest,
  getRoomScheduleInformationForRoomId,
  (_state: RootState, _buildingId: string, _roomId: string, start: string) => start,
  (roomAvailabilityPeriodOfInterest, scheduleInformation, start) =>
    getLastAboutToEndTimeslot(start, roomAvailabilityPeriodOfInterest, scheduleInformation),
)((_: RootState, roomId: string, start: string) => `${roomId}:${start}`);

/**
 * Look trough meeting room schedule data and finds first free timeslot for provided duration.
 * @returns Start and end of timeslot or `undefined` if no free timeslot could be found.
 */
export const getFirstAvailableTimeslot = createCachedSelector(
  getRoomScheduleInformationForRoomId,
  (_state: RootState, _buildingId: string, _roomId: string, startDateString: string, durationOfTimeslot: Duration) => ({
    startDateString,
    durationOfTimeslot,
  }),
  (scheduleInformation, {startDateString, durationOfTimeslot}): Timeslot | undefined => {
    const timeslots = scheduleInformation?.map(mapScheduleInformationToTimeslot);
    const start = parseISO(startDateString);

    if (!timeslots?.length)
      return {
        start,
        end: add(start, durationOfTimeslot),
      };

    const scheduleAfterWhichTimeslotCouldBeFitted = timeslots.find((currentSchedule, index) => {
      const nextSchedule = timeslots[index + 1];
      if (!nextSchedule) return true; // room is available after current schedule

      const startOfTimeslotToCheck = currentSchedule.end;
      const endOfTimeslotToCheck = add(startOfTimeslotToCheck, durationOfTimeslot);

      return isWithinInterval(endOfTimeslotToCheck, {start: startOfTimeslotToCheck, end: nextSchedule.start});
    });

    if (!scheduleAfterWhichTimeslotCouldBeFitted) return;

    const startOfTimeslot = scheduleAfterWhichTimeslotCouldBeFitted.end;
    return {
      start: startOfTimeslot,
      end: add(startOfTimeslot, durationOfTimeslot),
    };
  },
)(
  (
    _: RootState,
    roomId: string,
    startDateString: string,
    {years, weeks, months, days, hours, minutes, seconds}: Duration,
  ) => `${roomId}:${startDateString}:${years}:${weeks}:${months}:${days}:${hours}:${minutes}:${seconds}`,
);

export const getDynamicFreeTimeslot = createCachedSelector(
  getRoomAvailabilityPeriodOfInterest,
  getSuggestionDataForRoomId,
  (_state: RootState, _buildingId: string, _roomId: string, startDateString: string, durationOfTimeslot: Duration) => ({
    startDateString,
    durationOfTimeslot,
  }),
  (roomAvailabilityPeriodOfInterest, suggestionData, {startDateString, durationOfTimeslot}): Timeslot | undefined =>
    calculateDynamicFreeTimeslot(roomAvailabilityPeriodOfInterest, suggestionData, startDateString, durationOfTimeslot),
)(
  (
    _: RootState,
    roomId: string,
    startDateString: string,
    {years, weeks, months, days, hours, minutes, seconds}: Duration = {},
  ) => `${roomId}:${startDateString}:${years}:${weeks}:${months}:${days}:${hours}:${minutes}:${seconds}`,
);
